import { Box, styled } from "@mui/material";
import { Button, H2, P1 } from "./";

interface DashboardSectionProps {
    reverse?: boolean;
    title: string;
    description: string;
    buttonText?: string;
    imageSrc?: string;
    imageAlt?: string;
}

const Section = styled(Box, {
    shouldForwardProp: (prop) => prop !== "reverse",
})<{ reverse?: boolean }>(({ reverse }) => ({
    width: "100%",
    display: "flex",
    flexDirection: reverse ? "row-reverse" : "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "56px",
    "@media (max-width: 600px)": {
        flexDirection: "column-reverse",
        gap: "24px",
        alignItems: "flex-start",
    },
}));

const ContentColumn = styled(Box)(() => ({
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    maxWidth: "600px",
    flex: 1,
    "@media (max-width: 600px)": {
        maxWidth: "100%",
        gap: "12px",
    },
}));

const Title = styled(H2)(() => ({
    color: "var(--text-primary)",
    "@media (max-width: 600px)": {
        fontSize: "28px",
        lineHeight: "34px",
    },
}));

const Description = styled(P1)(() => ({
    color: "var(--text-tertiary)",
    "@media (max-width: 600px)": {
        fontSize: "16px",
        lineHeight: "24px",
    },
}));

const ImageColumn = styled(Box)(() => ({
    flex: 1,
    maxWidth: "600px",
    borderRadius: "32px",
    overflow: "hidden",
    backgroundColor: "var(--background-content)",
    minHeight: "400px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "@media (max-width: 600px)": {
        maxWidth: "100%",
        minHeight: "auto",
        aspectRatio: "343/260", // Approximate mobile aspect ratio from design if needed, or just let content flow
    },
}));

const StyledImage = styled("img")(() => ({
    width: "100%",
    height: "100%",
    objectFit: "cover",
}));

export const DashboardSection = ({
    reverse = false,
    title,
    description,
    buttonText = "Попробовать бесплатно",
    imageSrc = "/assets/dashboard-preview.png",
    imageAlt = "Dashboard Preview"
}: DashboardSectionProps) => {
    return (
        <Section reverse={reverse}>
            <ContentColumn>
                <Title>{title}</Title>
                <Description>{description}</Description>
                <Box>
                    <Button variant="text">
                        {buttonText}
                    </Button>
                </Box>
            </ContentColumn>
            <ImageColumn>
                <StyledImage
                    src={imageSrc}
                    alt={imageAlt}
                    onError={(e) => {
                        e.currentTarget.style.display = 'none';
                    }}
                />
            </ImageColumn>
        </Section>
    );
};
